import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const PieceDetailsForm = ({ 
  weight, 
  onWeightChange, 
  cutType, 
  onCutTypeChange,
  productId,
  onProductChange,
  errors,
  // new unified props
  formData,
  onChange
}) => {
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [productOpen, setProductOpen] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [gestionOpen, setGestionOpen] = useState(false);
  const productRef = useRef(null);
  const gestionRef = useRef(null);

  // Support both old and new prop patterns
  const _weight = weight ?? formData?.weight ?? '';
  const _cutType = cutType ?? formData?.cutType ?? '';
  const _productId = productId ?? formData?.productId ?? null;

  const _onWeightChange = onWeightChange ?? ((v) => onChange?.('weight', v));
  const _onCutTypeChange = onCutTypeChange ?? ((v) => onChange?.('cutType', v));
  const _onProductChange = onProductChange ?? ((v) => onChange?.('productId', v));

  useEffect(() => {
    loadProducts();
  }, []);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (productRef?.current && !productRef?.current?.contains(event?.target)) {
        setProductOpen(false);
      }
      if (gestionRef?.current && !gestionRef?.current?.contains(event?.target)) {
        setGestionOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadProducts = async () => {
    try {
      setLoadingProducts(true);
      const { data, error } = await supabase
        ?.from('productos')
        ?.select('id, nombre, categoria, activo')
        ?.eq('activo', true)
        ?.order('nombre');
      if (error) throw error;
      setProducts(data || []);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoadingProducts(false);
    }
  };

  const productOptions = products
    ?.filter(p => p?.activo)
    ?.map(product => ({
      value: product?.id,
      label: product?.nombre,
      description: product?.categoria === 'jamon' ? 'Jamón' : 'Paletilla'
    }));

  const filteredProducts = productOptions?.filter(p =>
    p?.label?.toLowerCase()?.includes(productSearch?.toLowerCase())
  );

  const cutTypeOptions = [
    { value: 'EMPLATADO', label: 'Emplatado', description: 'Corte premium para presentación — coste cortador: €25 fijo', icon: 'Star', color: '#D6EF4E' },
    { value: 'NORMAL', label: 'Normal', description: 'Corte estándar — coste cortador: <10kg=€20 / ≥10kg=€25', icon: 'Scissors', color: '#3A7F6B' },
    { value: 'GRANDE_Y_EMPLATADO', label: 'Grande y Emplatado', description: 'Pieza grande con emplatado — coste cortador: €30 fijo', icon: 'Award', color: '#C9A24D' },
    { value: 'PALETILLA', label: 'Paletilla', description: 'Corte de paletilla — coste cortador: €15 fijo', icon: 'Package', color: '#8097D6' },
    { value: 'TUPPER_CON_GRASA', label: 'Tupper con Grasa', description: 'Sin envasado al vacío, cortador añade la grasa — coste: €30 fijo', icon: 'Container', color: '#E05252' },
  ];

  const selectedProduct = productOptions?.find(p => p?.value === _productId);
  const selectedCutType = cutTypeOptions?.find(c => c?.value === _cutType);

  const getWeightStatus = () => {
    const w = parseFloat(_weight);
    if (isNaN(w) || w <= 0) return null;
    if (w < 10) return { color: 'warning', icon: 'AlertTriangle', message: 'Peso < 10 kg — NORMAL: coste cortador €20 (el peso determina el rendimiento, no la tarifa al cliente)' };
    return { color: 'success', icon: 'CheckCircle', message: 'Peso ≥ 10 kg — NORMAL: coste cortador €25 (el peso determina el rendimiento, no la tarifa al cliente)' };
  };

  const weightStatus = getWeightStatus();

  const inputClass = "w-full bg-[#111111] border border-white/10 text-white/85 text-sm rounded-lg px-3 py-2.5 font-oranienbaum placeholder-white/25 focus:outline-none focus:border-[#D6EF4E]/40 transition-colors";
  const labelClass = "block text-[19px] font-oranienbaum text-white mb-1.5";
  const errorClass = "text-[11px] text-[#E05252] mt-1 font-oranienbaum";

  return (
    <div className="space-y-4">
      {/* Producto — custom dropdown */}
      <div ref={productRef} className="relative">
        <div className="flex items-center justify-between mb-1.5">
          <label className={labelClass}>Producto</label>
          <Link
            to="/product-management"
            className="text-[11px] font-oranienbaum text-[#D6EF4E]/70 hover:text-[#D6EF4E] flex items-center gap-1 transition-colors"
            target="_blank"
          >
            <Icon name="ExternalLink" size={11} />
            Gestionar
          </Link>
        </div>
        <button
          type="button"
          onClick={() => setProductOpen(!productOpen)}
          disabled={loadingProducts}
          className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg border transition-colors focus:outline-none
            ${errors?.productId ? 'border-[#E05252]/50' : 'border-white/10 hover:border-white/20 focus:border-[#D6EF4E]/40'}
            ${loadingProducts ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            bg-[#111111]`}
        >
          {loadingProducts ? (
            <span className="text-sm font-oranienbaum text-white/30">Cargando productos...</span>
          ) : selectedProduct ? (
            <div className="flex items-center gap-2.5 flex-1 text-left min-w-0">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(214,239,78,0.1)' }}>
                <Icon name="Package" size={14} style={{ color: '#D6EF4E' }} />
              </div>
              <span className="text-sm font-oranienbaum text-white/85 truncate">{selectedProduct?.label}</span>
            </div>
          ) : (
            <span className="text-sm font-oranienbaum text-[rgba(255,255,255,0.6)]">Seleccionar producto...</span>
          )}
          <Icon name={productOpen ? 'ChevronUp' : 'ChevronDown'} size={14} style={{ color: 'rgba(255,255,255,0.35)', flexShrink: 0, marginLeft: 8 }} />
        </button>
        {errors?.productId && <p className={errorClass}>{errors?.productId}</p>}

        {/* Selected product info */}
        {selectedProduct && (
          <div className="mt-2 p-3 bg-[#111111] rounded-lg border border-white/8">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(214,239,78,0.1)' }}>
                <Icon name="Package" size={13} style={{ color: '#D6EF4E' }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-oranienbaum font-semibold text-white/85 truncate">{selectedProduct?.label}</p>
                <p className="text-[10px] font-oranienbaum text-white/35">{selectedProduct?.description}</p>
              </div>
            </div>
          </div>
        )}

        {/* Product dropdown */}
        {productOpen && (
          <div className="absolute z-50 w-full mt-1.5 bg-[#1A1A1A] rounded-xl shadow-2xl border border-white/10 overflow-hidden">
            <div className="p-2.5 border-b border-white/8">
              <div className="relative">
                <Icon name="Search" size={13} style={{ color: 'rgba(255,255,255,0.35)', position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }} />
                <input
                  type="text"
                  placeholder="Buscar producto..."
                  value={productSearch}
                  onChange={(e) => setProductSearch(e?.target?.value)}
                  className="w-full bg-[#111111] border border-white/10 text-white/85 text-xs rounded-lg pl-8 pr-3 py-2 font-oranienbaum placeholder-white/25 focus:outline-none focus:border-[#D6EF4E]/40 transition-colors"
                />
              </div>
            </div>
            <div className="max-h-64 overflow-y-auto">
              {filteredProducts?.length > 0 ? (
                filteredProducts?.map((opt) => (
                  <button
                    key={opt?.value}
                    type="button"
                    onClick={() => {
                      _onProductChange(opt?.value);
                      setProductOpen(false);
                      setProductSearch('');
                    }}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 transition-colors text-left
                      ${_productId === opt?.value ? 'bg-[#D6EF4E]/8' : 'hover:bg-white/5'}`}
                  >
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(214,239,78,0.08)' }}>
                      <Icon name="Package" size={13} style={{ color: '#D6EF4E' }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-oranienbaum text-white/85 truncate">{opt?.label}</p>
                      <p className="text-[10px] font-oranienbaum text-white/35 truncate">{opt?.description}</p>
                    </div>
                    {_productId === opt?.value && (
                      <Icon name="Check" size={13} style={{ color: '#D6EF4E', flexShrink: 0 }} />
                    )}
                  </button>
                ))
              ) : (
                <div className="p-6 text-center">
                  <Icon name="Search" size={24} style={{ color: 'rgba(255,255,255,0.2)', margin: '0 auto 8px' }} />
                  <p className="text-xs font-oranienbaum text-white/35">No se encontraron productos</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Peso */}
      <div>
        <label className={labelClass}>Peso (kg) *</label>
        <input
          type="number"
          className={`${inputClass} ${errors?.weight ? 'border-[#E05252]/50' : ''}`}
          placeholder="0.00"
          value={_weight}
          onChange={(e) => _onWeightChange(e?.target?.value)}
          min="0.01"
          max="50"
          step="0.01"
        />
        <p className="text-[10px] font-oranienbaum text-white mt-1">Ingrese el peso en kilogramos con hasta 2 decimales</p>
        {errors?.weight && <p className={errorClass}>{errors?.weight}</p>}
        {weightStatus && (
          <div className={`mt-2 p-2.5 rounded-lg flex items-center gap-2 border ${
            weightStatus?.color === 'warning' ? 'bg-[#C9A24D]/8 border-[#C9A24D]/20' : 'bg-[#3A7F6B]/8 border-[#3A7F6B]/20'
          }`}>
            <Icon
              name={weightStatus?.icon}
              size={14}
              style={{ color: weightStatus?.color === 'warning' ? '#C9A24D' : '#3A7F6B', flexShrink: 0 }}
            />
            <p className="text-xs font-oranienbaum" style={{ color: weightStatus?.color === 'warning' ? '#C9A24D' : '#3A7F6B' }}>
              {weightStatus?.message}
            </p>
          </div>
        )}
      </div>

      {/* Tipo de Gestión — custom dropdown */}
      <div ref={gestionRef} className="relative">
        <label className={labelClass}>Tipo de Gestión *</label>
        <button
          type="button"
          onClick={() => setGestionOpen(!gestionOpen)}
          className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg border transition-colors focus:outline-none cursor-pointer
            ${errors?.cutType ? 'border-[#E05252]/50' : 'border-white/10 hover:border-white/20 focus:border-[#D6EF4E]/40'}
            bg-[#111111]`}
        >
          {selectedCutType ? (
            <div className="flex items-center gap-2.5 flex-1 text-left min-w-0">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `rgba(${selectedCutType?.color === '#D6EF4E' ? '214,239,78' : selectedCutType?.color === '#3A7F6B' ? '58,127,107' : '128,151,214'},0.1)` }}>
                <Icon name={selectedCutType?.icon} size={14} style={{ color: selectedCutType?.color }} />
              </div>
              <span className="text-sm font-oranienbaum text-white/85 truncate">{selectedCutType?.label}</span>
            </div>
          ) : (
            <span className="text-sm font-oranienbaum text-[rgba(255,255,255,0.702)]">Seleccionar tipo...</span>
          )}
          <Icon name={gestionOpen ? 'ChevronUp' : 'ChevronDown'} size={14} style={{ color: 'rgba(255,255,255,0.35)', flexShrink: 0, marginLeft: 8 }} />
        </button>
        {errors?.cutType && <p className={errorClass}>{errors?.cutType}</p>}

        {/* Selected cut type info */}
        {selectedCutType && (
          <div className="mt-2 p-2.5 rounded-lg flex items-center gap-2 border"
            style={{
              background: `rgba(${selectedCutType?.color === '#D6EF4E' ? '214,239,78' : selectedCutType?.color === '#3A7F6B' ? '58,127,107' : '128,151,214'},0.08)`,
              borderColor: `rgba(${selectedCutType?.color === '#D6EF4E' ? '214,239,78' : selectedCutType?.color === '#3A7F6B' ? '58,127,107' : '128,151,214'},0.2)`
            }}>
            <Icon name={selectedCutType?.icon} size={14} style={{ color: selectedCutType?.color, flexShrink: 0 }} />
            <p className="text-xs font-oranienbaum font-semibold" style={{ color: selectedCutType?.color }}>
              {selectedCutType?.label} — {selectedCutType?.description}
            </p>
          </div>
        )}

        {/* Gestion dropdown */}
        {gestionOpen && (
          <div className="absolute z-50 w-full mt-1.5 bg-[#1A1A1A] rounded-xl shadow-2xl border border-white/10 overflow-hidden">
            <div className="max-h-64 overflow-y-auto">
              {cutTypeOptions?.map((opt) => (
                <button
                  key={opt?.value}
                  type="button"
                  onClick={() => {
                    _onCutTypeChange(opt?.value);
                    setGestionOpen(false);
                  }}
                  className={`w-full flex items-center gap-2.5 px-3 py-3 transition-colors text-left
                    ${_cutType === opt?.value ? 'bg-[#D6EF4E]/8' : 'hover:bg-white/5'}`}
                >
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: `rgba(${opt?.color === '#D6EF4E' ? '214,239,78' : opt?.color === '#3A7F6B' ? '58,127,107' : '128,151,214'},0.1)` }}>
                    <Icon name={opt?.icon} size={13} style={{ color: opt?.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-oranienbaum text-white/85">{opt?.label}</p>
                    <p className="text-[10px] font-oranienbaum text-white/35 truncate">{opt?.description}</p>
                  </div>
                  {_cutType === opt?.value && (
                    <Icon name="Check" size={13} style={{ color: '#D6EF4E', flexShrink: 0 }} />
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Tariff info */}
      <div className="p-2.5 bg-white/3 border border-white/8 rounded-lg">
        <div className="flex items-start gap-2">
          <Icon name="Info" size={13} style={{ color: 'rgba(255,255,255,0.35)', flexShrink: 0, marginTop: 1 }} />
          <div>
            <p className="text-[13px] font-oranienbaum font-semibold text-white mb-1">Cálculo de Tarifa</p>
            <ul className="space-y-0.5 text-[10px] font-oranienbaum text-white/70">
              <li>• Normal: Peso &lt;10kg = €20, Peso ≥10kg = €25</li>
              <li>• Emplatado: €25 (tarifa fija)</li>
              <li>• Grande y Emplatado: €30 (tarifa fija)</li>
              <li>• Paletilla: €15 (tarifa fija)</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PieceDetailsForm;